import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { Suggest } from '@ubilabs/react-geosuggest';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Co2FreeIcon from '@/assets/deliveryTypes/Co2FreeIcon';
import Icon99Minutos from '@/assets/deliveryTypes/Icon99Minutos';
import NationalIcon from '@/assets/deliveryTypes/NationalIcon';
import NextDayIcon from '@/assets/deliveryTypes/NexdayIcon';
import ReturnIcon from '@/assets/deliveryTypes/ReturnIcon';
import SameDayIcon from '@/assets/deliveryTypes/SameDayIcon';
import i18n from '@/i18n/translations';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { DeliveryTypes, IResQuotationOK } from '@/types/parcel/createOrder/quotation.interface';
import { handleTranslations } from '@/utils/common';

interface Address extends Partial<Suggest> {
  codePostal: string | string[];
  description: string;
}

interface IQuotation {
  loading: boolean;
  error: string | null;
  data: DeliveryTypes[] | null;
}

const useCoverage = () => {
  const [address, setAddress] = useState<{ origin: Address | null; destination: Address | null }>({
    origin: null,
    destination: null,
  });
  const [needsManualInput, setNeedsManualInput] = useState<{
    origin: boolean;
    destination: boolean;
  }>({
    origin: false,
    destination: false,
  });
  const [quot, setQuot] = useState<IQuotation>({
    loading: false,
    error: null,
    data: null,
  });

  const { profile } = useSelector((state: RootState) => state.parcel);

  const handleAddress = (name: string, value: Address | null) => {
    // Check if this address needs manual postal code input for ALL countries
    const needsManual = value?.codePostal === 'MISSING' || value?.codePostal === '';

    // Normalize empty or MISSING postal codes to 'MISSING' for all countries
    // This handles both MEX (which sends 'MISSING') and other countries (which send '')
    const normalizedValue = needsManual && value ? { ...value, codePostal: 'MISSING' } : value;

    // Single state update with normalized postal code
    setAddress((prev) => ({ ...prev, [name]: normalizedValue }));

    // Update manual input flag
    setNeedsManualInput((prev) => ({ ...prev, [name]: needsManual }));
  };

  const handleManualPostalCode = (name: 'origin' | 'destination', value: string) => {
    // Only allow numbers and limit length based on country
    const maxLength = getPostalCodeMaxLength();
    const numericValue = value.replace(/\D/g, '').slice(0, maxLength);

    // Update the address object with the manual postal code
    setAddress((prev) => {
      if (!prev[name]) return prev;

      return {
        ...prev,
        [name]: {
          ...prev[name],
          codePostal: numericValue || 'MISSING', // Keep MISSING if empty
        },
      };
    });
  };

  const getPostalCodeMaxLength = (): number => {
    const country = profile.data?.country;
    switch (country) {
      case 'MEX':
        return 5;
      case 'CHL':
        return 7;
      case 'COL':
        return 6;
      case 'PER':
        return 5;
      default:
        return 10;
    }
  };

  const getQuotation = async () => {
    const http = initializeHttpProvider();
    try {
      setQuot((prev) => ({ ...prev, loading: true }));

      const response = await http.createOrder.quotation({
        origin: {
          address: address.origin?.label,
          country: profile.data?.country || '',
          latitude: address.origin?.location?.lat.toString() || '0',
          longitude: address.origin?.location?.lng.toString() || '0',
          zipCode: (address.origin?.codePostal as string) || '',
        },
        destination: {
          address: address.destination?.label,
          country: profile.data?.country || '',
          latitude: address.destination?.location?.lat.toString() || '0',
          longitude: address.destination?.location?.lng.toString() || '0',
          zipCode: (address.destination?.codePostal as string) || '',
        },
      });
      const deliveryTypes = (response as IResQuotationOK).data.deliveryTypes;

      const hasReturnOrSameDay = deliveryTypes.some(
        (dt) => dt.deliveryTypeCode === 'RET' || dt.deliveryTypeCode === 'SMD',
      );
      const filteredDeliveryTypes = hasReturnOrSameDay
        ? deliveryTypes.filter((dt) => dt.deliveryTypeCode !== 'NAL')
        : deliveryTypes;

      setQuot((prev) => ({
        ...prev,
        error: null,
        data: filteredDeliveryTypes,
      }));
    } catch (e: unknown) {
      setQuot((prev) => ({
        ...prev,
        error: e instanceof Error ? e.message : 'Error al obtener la cotización',
        data: null,
      }));
    } finally {
      setQuot((prev) => ({ ...prev, loading: false }));
    }
  };

  const hasValidPostalCodes = () => {
    const originValid = address.origin?.codePostal && address.origin.codePostal !== 'MISSING';
    const destinationValid =
      address.destination?.codePostal && address.destination.codePostal !== 'MISSING';

    return originValid && destinationValid;
  };

  const shouldShowManualInput = (addressType: 'origin' | 'destination'): boolean => {
    return needsManualInput[addressType];
  };

  const getManualPostalCodeValue = (addressType: 'origin' | 'destination'): string => {
    const postalCode = address[addressType]?.codePostal;
    if (postalCode === 'MISSING' || !postalCode) {
      return '';
    }
    return (postalCode as string) || '';
  };

  const disableButton =
    !address.origin || !address.destination || quot.loading || !hasValidPostalCodes();

  useEffect(() => {
    const translation = handleTranslations(profile.data?.country ?? 'MEX');
    i18n
      .changeLanguage(translation)
      .then((newt) => {
        newt('currencySymbol');
        newt('currencyNomenclature');
        newt('originPlaceHolder');
        newt('destinationPlaceHolder');
        newt('coverageAdvice');
      })
      .catch(() => {});
  }, []);

  const handleDeliveryIcon = (
    deliveryType: string,
  ): OverridableComponent<SvgIconTypeMap<{}, 'svg'>> => {
    switch (deliveryType) {
      case 'SPT':
        return NextDayIcon;
      case 'NXD':
        return NextDayIcon;
      case '99M':
        return Icon99Minutos;
      case 'EXP':
        return Icon99Minutos;
      case 'CO2':
        return Co2FreeIcon;
      case 'NAL':
        return NationalIcon;
      case 'RET':
        return ReturnIcon;
      case 'SMD':
        return SameDayIcon;
      default:
        return NextDayIcon;
    }
  };

  const getPostalCodePlaceholder = (): string => {
    const country = profile.data?.country;
    switch (country) {
      case 'MEX':
        return '03100';
      case 'CHL':
        return '8320000';
      case 'COL':
        return '110111';
      case 'PER':
        return '15001';
      default:
        return 'Ingresa tu código postal';
    }
  };

  return {
    address,
    handleAddress,
    disableButton,
    getQuotation,
    quot,
    country: profile.data?.country,
    handleDeliveryIcon,
    postalCodePlaceholder: getPostalCodePlaceholder(),
    handleManualPostalCode,
    shouldShowManualInput,
    getPostalCodeMaxLength,
    getManualPostalCodeValue,
  };
};

export default useCoverage;
